import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, MapPin, Star, 
  Users, TrendingUp, MessageSquare, RotateCcw, LogOut,
  Menu, X
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    pendingReturns: 0,
    newLeads: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Load basic stats
      const [ordersRes, productsRes, leadsRes, returnsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/products'),
        api.get('/admin/crm/leads?status=new'),
        api.get('/admin/returns?status=requested'),
      ]);

      const orders = ordersRes.data;
      const totalRevenue = orders.reduce((sum, order) => sum + order.amount, 0);

      setStats({
        totalOrders: orders.length,
        totalRevenue,
        totalProducts: productsRes.data.length,
        pendingReturns: returnsRes.data.length,
        newLeads: leadsRes.data.length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/hidden/admin');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/hidden/admin/dashboard', active: true },
    { icon: Package, label: 'Products', path: '/hidden/admin/products' },
    { icon: ShoppingCart, label: 'Orders', path: '/hidden/admin/orders' },
    { icon: MapPin, label: 'Pincodes', path: '/hidden/admin/pincodes' },
    { icon: Star, label: 'Testimonials', path: '/hidden/admin/testimonials' },
    { icon: MessageSquare, label: 'CRM Leads', path: '/hidden/admin/crm' },
    { icon: RotateCcw, label: 'Returns', path: '/hidden/admin/returns' },
    { icon: TrendingUp, label: 'Analytics', path: '/hidden/admin/analytics' },
    { icon: Users, label: 'Users', path: '/hidden/admin/users' },
  ];

  return (
    <div className="min-h-screen bg-brand-timberwolf" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-brand-pearl text-white transition-transform duration-300 z-50 ${sidebarOpen ? 'w-64' : 'w-0'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-sm border-2 border-white flex items-center justify-center">
                <span className="text-xl font-heading font-bold">R</span>
              </div>
              <div>
                <div className="font-heading font-bold">Admin Panel</div>
                <div className="text-xs opacity-80">{admin?.name}</div>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
                  item.active
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 mt-8 w-full text-white/70 hover:bg-white/10 hover:text-white rounded-sm transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'ml-0'}`}>
        {/* Header */}
        <header className="bg-white border-b border-brand-pearl/10 sticky top-0 z-40">
          <div className="px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-brand-timberwolf rounded-sm transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-heading font-bold text-brand-pearl">Dashboard Overview</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-brand-walnut">Role: {admin?.role}</span>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-sm border border-brand-pearl/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <ShoppingCart className="w-8 h-8 text-brand-pearl" />
                <span className="text-xs text-brand-walnut">Total</span>
              </div>
              <div className="text-3xl font-bold text-brand-pearl">{stats.totalOrders}</div>
              <div className="text-sm text-brand-walnut">Orders</div>
            </div>

            <div className="bg-white rounded-sm border border-brand-pearl/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-brand-green" />
                <span className="text-xs text-brand-walnut">Revenue</span>
              </div>
              <div className="text-3xl font-bold text-brand-pearl">₹{stats.totalRevenue.toLocaleString()}</div>
              <div className="text-sm text-brand-walnut">Total Revenue</div>
            </div>

            <div className="bg-white rounded-sm border border-brand-pearl/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <Package className="w-8 h-8 text-brand-walnut" />
                <span className="text-xs text-brand-walnut">Catalog</span>
              </div>
              <div className="text-3xl font-bold text-brand-pearl">{stats.totalProducts}</div>
              <div className="text-sm text-brand-walnut">Products</div>
            </div>

            <div className="bg-white rounded-sm border border-brand-pearl/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <MessageSquare className="w-8 h-8 text-brand-wine" />
                <span className="text-xs text-brand-walnut">Pending</span>
              </div>
              <div className="text-3xl font-bold text-brand-pearl">{stats.newLeads}</div>
              <div className="text-sm text-brand-walnut">New Leads</div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-sm border border-brand-pearl/10 p-6 mb-8">
            <h2 className="text-xl font-heading font-bold text-brand-pearl mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to="/hidden/admin/products"
                className="flex flex-col items-center p-4 border-2 border-brand-pearl/20 rounded-sm hover:border-brand-pearl hover:bg-brand-timberwolf transition-colors"
              >
                <Package className="w-8 h-8 text-brand-pearl mb-2" />
                <span className="text-sm font-medium text-brand-pearl">Manage Products</span>
              </Link>
              
              <Link
                to="/hidden/admin/orders"
                className="flex flex-col items-center p-4 border-2 border-brand-pearl/20 rounded-sm hover:border-brand-pearl hover:bg-brand-timberwolf transition-colors"
              >
                <ShoppingCart className="w-8 h-8 text-brand-pearl mb-2" />
                <span className="text-sm font-medium text-brand-pearl">View Orders</span>
              </Link>

              <Link
                to="/hidden/admin/crm"
                className="flex flex-col items-center p-4 border-2 border-brand-pearl/20 rounded-sm hover:border-brand-pearl hover:bg-brand-timberwolf transition-colors"
              >
                <MessageSquare className="w-8 h-8 text-brand-pearl mb-2" />
                <span className="text-sm font-medium text-brand-pearl">CRM Leads</span>
              </Link>

              <Link
                to="/hidden/admin/pincodes"
                className="flex flex-col items-center p-4 border-2 border-brand-pearl/20 rounded-sm hover:border-brand-pearl hover:bg-brand-timberwolf transition-colors"
              >
                <MapPin className="w-8 h-8 text-brand-pearl mb-2" />
                <span className="text-sm font-medium text-brand-pearl">Pincodes</span>
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-sm border border-brand-pearl/10 p-6">
            <h2 className="text-xl font-heading font-bold text-brand-pearl mb-4">System Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-brand-timberwolf rounded-sm">
                <span className="text-sm text-brand-walnut">Pending Returns</span>
                <span className="text-sm font-bold text-brand-pearl">{stats.pendingReturns}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-brand-timberwolf rounded-sm">
                <span className="text-sm text-brand-walnut">New Leads (Today)</span>
                <span className="text-sm font-bold text-brand-pearl">{stats.newLeads}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-brand-timberwolf rounded-sm">
                <span className="text-sm text-brand-walnut">Total Products</span>
                <span className="text-sm font-bold text-brand-pearl">{stats.totalProducts}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
